import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from '../RootState';
import {
    cancelSlotApi,
    createHighlightedSlotsApi,
    getPortfolioApi,
    manageSlotApi,
    updateValidityApi,
} from './api';
import {
    AssignmentResponse,
    CancelSlotPayload,
    CreateHighlightedSlotsPayload,
    HighlightedResponse,
    ManageSlotPairsPayload,
    ManageSlotPayload,
    PortfolioFullResult,
    PortfolioProperty,
    PortfolioResponse,
    PortfolioScope,
    PortfolioSlot,
    SlotsOrder,
    UpdateValidityPayload,
    UpdateValidityResponse,
} from './user_portfolio.types';
import { DEFAULT_PAGE_SIZE, FULL_LOAD_PAGE_SIZE } from './user_portfolio.constants';

/**
 * Carga el portafolio del usuario activo (`commercial_panel/portfolio`).
 *
 * Por defecto usa como `parameter` el correo del usuario buscado
 * (`shopping_cart.suggested.users.email`) y el `token` de sesión; ambos, junto a
 * `page`, pueden sobreescribirse por payload.
 */
export const getPortfolio = createAsyncThunk<
    PortfolioResponse,
    {
        parameter?: string;
        page?: number;
        /** Tamaño de página (`page_size`) server-side. */
        pageSize?: number;
        slotId?: string | number;
        propertyId?: string | number;
        /** Orden de slots por fecha de expiración (`slots_order`). */
        slotsOrder?: SlotsOrder | null;
        /** Qué sección actualizar; por defecto 'all'. */
        scope?: PortfolioScope;
    } | void,
    { state: RootState }
>('user_portfolio/getPortfolio', async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.User?.token ?? '';
    const portfolio = state.user_portfolio?.portfolio;
    const args = (payload ?? {}) as {
        parameter?: string;
        page?: number;
        pageSize?: number;
        slotId?: string | number;
        propertyId?: string | number;
        slotsOrder?: SlotsOrder | null;
        scope?: PortfolioScope;
    };
    const scope: PortfolioScope = args.scope ?? 'all';

    const parameter =
        args.parameter ?? portfolio?.loadedForParameter ?? state.shopping_cart.suggested.users.email;

    // Al cambiar de usuario se reinician paginación y filtros; con el mismo
    // usuario se preservan los valores no especificados de la sección.
    const isSameUser = !!portfolio?.loadedForParameter && parameter === portfolio.loadedForParameter;
    const keep = <T>(value: T | undefined, current: T | undefined, fallback: T): T =>
        value ?? (isSameUser ? current ?? fallback : fallback);

    // El backend usa un solo `page`: para el scope 'properties' se envía la
    // página de propiedades; en el resto, la de slots.
    const page =
        scope === 'properties'
            ? keep(args.page, portfolio?.propertyPage, 0)
            : keep(args.page, portfolio?.page, 0);
    // `page_size` es una preferencia del selector (no depende del usuario): se
    // envía siempre, usando el valor explícito, el último aplicado o el default.
    const pageSize = args.pageSize ?? portfolio?.pageSize ?? DEFAULT_PAGE_SIZE;
    const slotId = keep(args.slotId, portfolio?.slotFilter, '');
    const propertyId = keep(args.propertyId, portfolio?.propertyFilter, '');
    // El orden se preserva entre páginas/recargas del mismo usuario.
    const slotsOrder = keep(args.slotsOrder, portfolio?.slotsOrder, null);

    const result = await getPortfolioApi(
        token,
        parameter,
        page,
        slotId,
        propertyId,
        slotsOrder,
        pageSize,
    );
    return { ...result, scope };
});

/** Elimina duplicados por `key` conservando el primero encontrado. */
const dedupeBy = <T>(items: T[], key: (item: T) => number | string): T[] => {
    const seen = new Set<number | string>();
    const result: T[] = [];
    items.forEach((item) => {
        const id = key(item);
        if (!seen.has(id)) {
            seen.add(id);
            result.push(item);
        }
    });
    return result;
};

/**
 * Carga el portafolio COMPLETO del usuario (todas las páginas acumuladas) para
 * la vista de Asignaciones. El backend acota `page_size` a 100, así que se
 * recorren las páginas necesarias (1 request si hay ≤100 slots/propiedades) y se
 * acumulan slots y propiedades. Se guarda aparte de `portfolio` para no afectar
 * la paginación server-side de Vigencias.
 */
export const getPortfolioFull = createAsyncThunk<
    PortfolioFullResult,
    { parameter?: string } | void,
    { state: RootState }
>('user_portfolio/getPortfolioFull', async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.User?.token ?? '';
    const args = (payload ?? {}) as { parameter?: string };
    const parameter =
        args.parameter ??
        state.user_portfolio?.full?.loadedForParameter ??
        state.shopping_cart.suggested.users.email;

    // Primera página: además de datos, entrega los totales para saber cuántas
    // páginas más hay que pedir.
    const first = await getPortfolioApi(
        token,
        parameter,
        0,
        '',
        '',
        null,
        FULL_LOAD_PAGE_SIZE,
    );
    const totalSlots = first.data.totalSlots;
    const totalProperties = first.data.totalProperties;

    let slots: PortfolioSlot[] = [...first.data.slots];
    let properties: PortfolioProperty[] = [...first.data.properties];

    // El backend usa un único `page` para slots y propiedades, así que cada
    // página trae ambas secciones a la vez.
    const totalPages = Math.max(
        Math.ceil(totalSlots / FULL_LOAD_PAGE_SIZE),
        Math.ceil(totalProperties / FULL_LOAD_PAGE_SIZE),
    );

    for (let pageIndex = 1; pageIndex < totalPages; pageIndex += 1) {
        // eslint-disable-next-line no-await-in-loop
        const next = await getPortfolioApi(
            token,
            parameter,
            pageIndex,
            '',
            '',
            null,
            FULL_LOAD_PAGE_SIZE,
        );
        slots = slots.concat(next.data.slots);
        properties = properties.concat(next.data.properties);
    }

    return {
        parameter,
        slots: dedupeBy(slots, (slot) => slot.id),
        properties: dedupeBy(properties, (property) => property.id_property),
        totalSlots,
        totalProperties,
    };
});

/**
 * Recarga el portafolio desde el backend. Se usa como RED DE SEGURIDAD cuando una
 * mutación falla (para cubrir un posible guardado parcial en lotes) y tras crear
 * slots nuevos (cuyos ids no se pueden inferir en el front). En el camino feliz de
 * asignar/liberar/reemplazar NO se llama: el estado se actualiza de forma optimista
 * en el slice para evitar recargas de `commercial_panel/portfolio`.
 *
 * Refresca `portfolio` (Vigencias) solo si ya estaba cargado y `full`
 * (Asignaciones) solo si ya estaba cargado, cada uno con su propio parámetro.
 */
const reloadAfterMutation = (
    thunkAPI: {
        getState: () => RootState;
        dispatch: ThunkDispatch<RootState, unknown, AnyAction>;
    },
): void => {
    const state = thunkAPI.getState();

    // Vigencias (paginado): se recarga SOLO si ya estaba cargada (el usuario abrió
    // la pestaña), usando su propio parámetro. Si nunca se abrió, no se pide nada:
    // su carga perezosa traerá datos frescos al entrar. Evita una llamada de más
    // cuando se opera únicamente desde Asignaciones.
    const portfolioParameter = state.user_portfolio?.portfolio.loadedForParameter;
    if (portfolioParameter) {
        thunkAPI.dispatch(getPortfolio({ parameter: portfolioParameter, page: 0, scope: 'all' }));
    }

    // Asignaciones (conjunto completo): se recarga SIEMPRE que estuviera cargado,
    // usando exactamente el mismo parámetro con el que se cargó. No se acopla al
    // parámetro de Vigencias porque `full` se carga con el correo tal cual se
    // buscó (`submittedQuery`) y podría no coincidir con el normalizado del
    // carrito; ese desajuste dejaba los slots recién asignados sin reflejarse en
    // "Ocupados".
    const fullParameter = state.user_portfolio?.full?.loadedForParameter;
    if (fullParameter) {
        thunkAPI.dispatch(getPortfolioFull({ parameter: fullParameter }));
    }
};

/**
 * Cambia la vigencia de los slots seleccionados (`commercial_panel/validity`).
 *
 * No recarga el portafolio: el listado solo se obtiene en la carga inicial
 * (`useLazyPortfolio`), de modo que tras un cambio exitoso NO se vuelve a llamar
 * a `commercial_panel/portfolio`.
 */
export const updateValidity = createAsyncThunk<
    UpdateValidityResponse,
    UpdateValidityPayload,
    { state: RootState }
>('user_portfolio/updateValidity', async ({ slotIds, expirationDate, reason }, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.User?.token ?? '';

    const result = await updateValidityApi(token, {
        slot_id: slotIds,
        date_end: expirationDate,
        observations: reason,
    });

    return result;
});

/**
 * Asigna uno o más slots a una propiedad (`commercial_panel/slots/manage`) y,
 * si tiene éxito, recarga el portafolio para reflejar los cambios.
 */
export const manageSlot = createAsyncThunk<
    AssignmentResponse,
    ManageSlotPayload,
    { state: RootState }
>('user_portfolio/manageSlot', async ({ slotIds, propertyId, reason, oldPropertyId }, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.User?.token ?? '';

    try {
        return await manageSlotApi(token, {
            items: slotIds.map((slotId) => ({
                slot_id: slotId,
                property_id: propertyId,
                observations: reason,
                sales_panel: true,
                ...(oldPropertyId != null ? { old_property_id: oldPropertyId } : {}),
            })),
        });
        // Éxito: el slice aplica el cambio de forma optimista (sin recargar).
    } catch (error) {
        // Ante fallo, recarga de seguridad (cubre un posible guardado parcial).
        reloadAfterMutation(thunkAPI);
        throw error;
    }
});

/**
 * Asigna en lote distintos pares propiedad↔slot (varias propiedades a varios
 * slots) vía `commercial_panel/slots/manage` y recarga el portafolio al terminar.
 */
export const manageSlotPairs = createAsyncThunk<
    AssignmentResponse,
    ManageSlotPairsPayload,
    { state: RootState }
>('user_portfolio/manageSlotPairs', async ({ pairs, reason }, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.User?.token ?? '';

    try {
        return await manageSlotApi(token, {
            items: pairs.map(({ slotId, propertyId, oldPropertyId }) => ({
                slot_id: slotId,
                property_id: propertyId,
                observations: reason,
                sales_panel: true,
                ...(oldPropertyId != null ? { old_property_id: oldPropertyId } : {}),
            })),
        });
        // Éxito: el slice aplica el cambio de forma optimista (sin recargar).
    } catch (error) {
        // Ante fallo, recarga de seguridad (cubre un posible guardado parcial).
        reloadAfterMutation(thunkAPI);
        throw error;
    }
});

/**
 * Crea slots comerciales (destacado/prime) para el usuario activo
 * (`commercial_panel/highlighted`) y, si tiene éxito, recarga el portafolio para
 * reflejar los nuevos slots.
 */
export const createHighlightedSlots = createAsyncThunk<
    HighlightedResponse,
    CreateHighlightedSlotsPayload,
    { state: RootState }
>('user_portfolio/createHighlightedSlots', async ({ numberSlots, months, prime }, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.User?.token ?? '';
    const idUser =
        state.user_portfolio?.portfolio.data_user?.id ||
        state.shopping_cart.suggested.users.user_id ||
        0;

    // El backend codifica el tipo de slot: 1 = destacado, 8 = prime.
    const result = await createHighlightedSlotsApi(token, {
        slots: numberSlots,
        months,
        id_user: idUser,
        highlighted_type: prime ? 8 : 1,
    });

    reloadAfterMutation(thunkAPI);

    return result;
});

/**
 * Libera (cancela la asignación de) uno o más slots (`commercial_panel/cancel`)
 * y, si tiene éxito, recarga el portafolio para reflejar los cambios.
 */
export const cancelSlot = createAsyncThunk<
    AssignmentResponse,
    CancelSlotPayload,
    { state: RootState }
>('user_portfolio/cancelSlot', async ({ slotIds, propertyIds, reason }, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.User?.token ?? '';

    try {
        return await cancelSlotApi(token, {
            properties_ids: propertyIds ?? [],
            slots_ids: slotIds,
            observations: reason,
            sales_panel: true,
        });
        // Éxito: el slice aplica el cambio de forma optimista (sin recargar).
    } catch (error) {
        // Ante fallo, recarga de seguridad (cubre un posible guardado parcial).
        reloadAfterMutation(thunkAPI);
        throw error;
    }
});
